/**
 * Oynatıcı tercihleri — ses seviyesi, sessizlik ve oynatma hızı.
 *
 * ## Sorun (Tasarım V4, Öneri 4)
 *
 * `usePlayerControls` her parçada `volume = 1`, `playbackRate = 1` ile
 * başlıyordu. Kullanıcı gece sesi kısıp sıradaki parçaya geçtiğinde ses yine
 * tam açılıyordu. Aynı sorun video için de geçerliydi.
 *
 * ## Kural
 *
 * Tercih `localStorage`'da `pbfork.player.v1` anahtarında saklanır. Geçersiz
 * kayıt (bozuk JSON, aralık dışı değer) güvenli varsayılana düşer; depolama
 * kullanılamıyorsa (gizli sekme, SSR) oynatıcı yine çalışır.
 */

import { recordSwallowed } from './swallowed.ts';

export interface PlayerPrefs {
  /** 0–1 arası ses düzeyi. */
  volume: number;
  muted: boolean;
  /** Oynatma hızı; yalnız desteklenen kademeler. */
  rate: number;
}

const STORAGE_KEY = 'pbfork.player.v1';

/** Arayüzdeki hız seçenekleriyle aynı liste. */
export const PLAYER_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;

const DEFAULT_PREFS: PlayerPrefs = { volume: 1, muted: false, rate: 1 };

function clampVolume(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_PREFS.volume;
  return Math.min(1, Math.max(0, value));
}

function normalizeRate(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_PREFS.rate;
  return (PLAYER_RATES as readonly number[]).includes(value) ? value : DEFAULT_PREFS.rate;
}

/** Kayıtlı tercihleri okur; yoksa/bozuksa güvenli varsayılanı döner. */
export function loadPlayerPrefs(): PlayerPrefs {
  if (typeof localStorage === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<PlayerPrefs>;
    return {
      volume: clampVolume(parsed.volume),
      muted: parsed.muted === true,
      rate: normalizeRate(parsed.rate),
    };
  } catch (error) {
    recordSwallowed('playerPrefs', error);
    return DEFAULT_PREFS;
  }
}

/** Tercihleri yazar; depolama hatası oynatmayı bozmaz. */
export function savePlayerPrefs(prefs: PlayerPrefs): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        volume: clampVolume(prefs.volume),
        muted: prefs.muted === true,
        rate: normalizeRate(prefs.rate),
      }),
    );
  } catch (error) {
    recordSwallowed('playerPrefs', error);
    // Depolama dolu/kapalı olabilir; oynatma devam eder.
  }
}
